import { Banner } from 'component/banner';
import { FlowBox, Pad, PadBox, WindowTitle } from 'component/basics';
import { ActionButton } from 'component/button';
import { colorTextOnTagLight } from 'component/color';
import { ConversationScreen } from 'component/screen';
import { Heading, UtilityText } from 'component/text';
import { View } from 'react-native';
import { useHasCapability, useIsAdmin } from 'util/admin';
import { useDatastore } from 'util/datastore';
import { useConfig } from 'util/features';
import type { Structure, TLabel } from 'util/stdtypes';
import { labelToKey, t } from 'util/stdtypes';

export type QuickLink = { label: TLabel; structureKey?: string; screenKey?: string; capability: string };

export type AdminConfig = {
    quickLinks?: QuickLink[];
    panels?: React.ComponentType[];
};

export const AdminStructure: Structure<AdminConfig> = {
    key: 'admin',
    name: 'Admin Dashboard',
    screen: AdminScreen,
    hasFocusTrap: true,
    isAdminUi: true,
    receivesConfigDefaults: {
        quickLinks: [{ label: t('Component Demo'), structureKey: 'componentdemo', capability: 'admin/componentdemo' }],
    },
};

export function AdminScreen() {
    const { quickLinks } = useConfig<AdminConfig>();
    const isAdmin = useIsAdmin();

    if (!isAdmin) {
        return (
            <Banner>
                <UtilityText color={colorTextOnTagLight} label={t('Admin Only')} />
            </Banner>
        );
    }

    return (
        <ConversationScreen pad>
            <WindowTitle label={t('Admin Dashboard')} />
            <Pad />
            <Heading label={t('Admin Dashboard')} type='large' weight='medium' />
            <Pad />
            <Heading label={t('Quick Links')} />
            <View style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingTop: 8 }}>
                {quickLinks?.map((quickLink) => (
                    <QuickLink key={labelToKey(quickLink.label)} quickLink={quickLink} />
                ))}
            </View>
            <Pad />
        </ConversationScreen>
    );
}

function QuickLink({ quickLink }) {
    const { label, structureKey, screenKey, capability } = quickLink;
    const hasCapability = useHasCapability(capability);
    const datastore = useDatastore();
    if (capability && !hasCapability) {
        return null;
    }
    function onPress() {
        if (structureKey) {
            datastore.gotoInstance({ structureKey, instanceKey: 'one' });
        } else if (screenKey) {
            datastore.pushSubscreen(screenKey);
        }
    }
    return <ActionButton label={label} onPress={onPress} />;
}
